import React from 'react';

import { Trans, useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';
import { WeezHeader, Button, Card, Form, Message, Tooltip, SelectHierarchyLabel, SelectLabel, Section, Icon, TooltipHelp, ToastContext } from '@weezevent/nacre';
import { useForm } from '@weezevent/weezjs-core';
import { requiredField } from '../../utils/validators';
import MessageInformation from './messageInformation';
import Organization from '../../models/organization';
import useAppContext from '../../context/useAppContext';
import timezones from './../../timezones/timezones.json';
import { LANGUAGES } from '../../nacre/components/LanguageSelector';
import css from './organizerSettings.module.css';
import CurrencySelector from '../../components/rates/currencySelector';

const getDefaultLanguage = (organization, languages) => {
    // first we replace('_', '-')
    const defaultLocale = organization.default_locale.replace('_', '-').toLowerCase();
    // than we check if in languages there is defaultLocale
    if (languages.includes(defaultLocale)) {
        // if there is we return it
        return defaultLocale;
    }
    // if not we want to find in languages first language that corresponds to defaultLocale
    // and return it, or if find is undefined we return 'en-gb'
    return languages.find(lang => lang?.split('-')[0] === defaultLocale) || 'en-gb';
};
export default function Settings({ organization, bank, orgaSettings }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [context, setContext] = useAppContext();

    const [formSettings, setFormSettings, , settingsErrors] = useForm(
        {
            currency: organization.currency || 'EUR',
            timezone: organization.timezone || 'Europe/Paris',
            default_locale: getDefaultLanguage(organization, organization.languages),
            languages: organization.languages.map(item => (item === 'it' ? 'it-it' : item))
        },
        {
            currency: requiredField,
            timezone: requiredField
        },
        [organization]
    );

    let timezonesOptions = React.useMemo(() => {
        return timezones.map(v => ({ key: v, text: v, value: v }));
    }, [timezones]);

    const handleSubmit = React.useCallback(() => {
        let { currency, timezone, default_locale, languages } = formSettings;
        // onSubmit we transform 'it-it' to 'it' cause back expect only 'it'
        let _languages = [...languages];
        const index = _languages.indexOf('it-it');
        if (index > 0) {
            _languages.splice(index, 1, 'it');
        }
        if (default_locale === 'it-it') {
            default_locale = 'it';
        }
        const organisation = new Organization({
            id: organization.id,
            currency,
            timezone,
            default_locale,
            languages: _languages
        });

        organisation
            .save({
                organizationId: organization.id
            })
            .then(({ response }) => {
                setContext({ ...context, organization: response });
                toast.success(t('ticket.organiser.settings.saveSuccess'));
            })
            .catch(() => {
                toast.error(t('ticket.organiser.settings.saveError'));
            });
    }, [formSettings, organization, toast]);

    const options = React.useMemo(() => {
        return Object.entries(LANGUAGES).map(([key, value]) => {
            return {
                text: value,
                value: key.toLowerCase()
            };
        });
    }, []);

    const getLanguage = React.useCallback(
        lang => {
            return options.filter(option => lang === option.value)[0].text;
        },
        [options]
    );

    const removeLanguage = React.useCallback(
        value => {
            let _languages = formSettings.languages;
            const index = formSettings.languages.indexOf(value);
            _languages.splice(index, 1);
            setFormSettings('languages', _languages);
        },
        [formSettings]
    );

    const handleLabelChange = selected => {
        return selected.length ? t('ticket.organiser.settings.languages.selected', { count: selected.length }) : '';
    };

    const selected = React.useMemo(() => {
        return formSettings.languages;
    }, [formSettings]);

    const isDisabled = React.useMemo(() => {
        return Object.entries(settingsErrors).length > 0 || !formSettings.languages.includes(formSettings.default_locale);
    }, [settingsErrors, formSettings]);

    return (
        <>
            <WeezHeader
                title={t('ticket.organiser.settings.pageTitle')}
                rightComponent={[<Button primary key="primary" label={t('ticket.common.action.save')} onClick={handleSubmit} disabled={isDisabled} />]}
            >
                {t('ticket.organiser.settings.subTitle')}
            </WeezHeader>
            {!bank.iban && !bank.rbc_account && <MessageInformation organization={organization} />}
            <Card.FormStep
                title={t('ticket.organiser.settings.title')}
                rightComponent={
                    <Message type="tips" title={t('ticket.organiser.description.tips')} mini>
                        <Trans i18nKey="ticket.organiser.settings.tips" />
                    </Message>
                }
            >
                <>
                    <Form.Field>
                        <Form.Label label={t('ticket.organiser.settings.timezone')} />
                        <Form.Select
                            onChange={value => {
                                setFormSettings('timezone', value);
                            }}
                            placeholder={t('ticket.organiser.settings.timezone-placeholder')}
                            value={formSettings.timezone}
                            options={timezonesOptions}
                        />
                        <Form.FieldErrors errors={settingsErrors['timezone'] ? [t(settingsErrors['timezone'])] : ['']} />
                    </Form.Field>
                    <Form.Field>
                        <Form.Label label={t('ticket.organiser.settings.currency')} />
                        <Tooltip
                            className={css['currency-select-tooltip']}
                            title={t('ticket.organiser.settings.tooltip')}
                            triggerOnHover={orgaSettings.immutable_currency}
                            visible={orgaSettings.immutable_currency}
                        >
                            <CurrencySelector disabled={orgaSettings.immutable_currency} value={formSettings.currency} onChange={value => setFormSettings('currency', value)} />
                        </Tooltip>
                    </Form.Field>
                    <Form.FieldErrors errors={settingsErrors['currency'] ? [t(settingsErrors['currency'])] : ['']} />
                </>
            </Card.FormStep>
            <Card.FormStep title={t('ticket.organiser.settings.languages.title')} rightComponent={<></>}>
                <>
                    <SelectLabel
                        label={
                            <>
                                {t('ticket.organiser.settings.languages.default-title')}
                                <TooltipHelp title={t('ticket.organiser.settings.languages.default-tooltip')} />
                            </>
                        }
                        placeholder={t('ticket.organiser.settings.languages.language-search')}
                        options={options}
                        value={formSettings.default_locale}
                        onChange={value => setFormSettings('default_locale', value)}
                    />
                    <SelectHierarchyLabel
                        multi
                        options={options}
                        selected={selected}
                        onLabelSelected={handleLabelChange}
                        totalItems={formSettings.languages.length}
                        onChange={selected => setFormSettings('languages', selected)}
                        label={
                            <>
                                {t('ticket.organiser.settings.languages.language-title')}
                                <TooltipHelp title={t('ticket.organiser.settings.languages.language-tooltip')} />
                            </>
                        }
                        placeholder={t('ticket.organiser.settings.languages.language-placeholder')}
                        placeholderSearch={t('ticket.organiser.settings.languages.language-search')}
                        labelSelectAll={t('ticket.common.action.select-all')}
                        labelApply={t('ticket.common.action.select')}
                    />
                    {!formSettings.languages.includes(formSettings.default_locale) && (
                        <Message type="danger" title={t('ticket.organiser.settings.languages.message-title')}>
                            {t('ticket.organiser.settings.languages.message-body')}
                        </Message>
                    )}
                    {formSettings.languages.length > 0 && (
                        <Section>
                            {formSettings.languages.map(lang => {
                                return (
                                    <div key={lang} className={css['languages-container']}>
                                        <div>{getLanguage(lang)}</div>
                                        <div
                                            onClick={() => removeLanguage(lang)}
                                            className={lang === formSettings.default_locale ? css['languages-cross-default'] : css['languages-cross']}
                                        >
                                            <Icon name="cross" />
                                        </div>
                                    </div>
                                );
                            })}
                        </Section>
                    )}
                </>
            </Card.FormStep>
        </>
    );
}

Settings.propTypes = {
    organization: PropTypes.object.isRequired,
    bank: PropTypes.object.isRequired,
    orgaSettings: PropTypes.object.isRequired
};
